import { useMatch } from 'react-router-dom';
import { DocService, ValidationTaskV3 } from '../../../services/dataService';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Collapse, Form, Input, message, Modal, Pagination, Radio, Select } from 'antd';
import { Dna01Icon, Medicine02Icon } from '../../components/icons';
import { Button } from '../../components/button';
import { useAuth } from '../../../authContext';
// import "./index.css";
import { ExtractedDetails } from '../dashboard/components/articleView';
import { CloseCircleOutlined } from '@ant-design/icons';
import { useState } from 'react';
import GenericForm from './genericForm';
import DrugSpecificForm from './drugSpecificForm';
import { Link2Icon, ArrowRightIcon } from 'lucide-react';

const STUDY_TYPE_OPTIONS = [
  { label: 'Preclinical', value: 'Preclinical' },
  { label: 'Case Study', value: 'Case Study' },
  { label: 'Clinical Trial: non-RCT', value: 'Clinical Trial: non-RCT' },
  { label: 'Clinical Trial: RCT', value: 'Clinical Trial: RCT' },
  { label: 'Observational study', value: 'Observational study' },
  { label: 'Review: meta-analysis', value: 'Review: meta-analysis' },
  { label: 'Review: other', value: 'Review: other' },
];

const THERAPEUTIC_ASSOCIATION_OPTIONS = [
  { label: 'Positive', value: 'Positive' },
  { label: 'Negative', value: 'Negative' },
  { label: 'No Benefit', value: 'No Benefit' },
  { label: 'Inconclusive', value: 'Inconclusive' },
  { label: 'Not mentioned', value: 'Not mentioned' },
];

const COMBINATION_OPTIONS = [
  { label: 'Original', value: 'Original' },
  { label: 'Combination', value: 'Combination' },
];

const TISSUE_STAGE_OPTIONS = [
  { label: 'Early', value: 'Early' },
  { label: 'Advanced', value: 'Advanced' },
  { label: 'N/A', value: 'N/A' },
];

const ClearableRadio = ({ value, onChange, children, onClear }) => {
  return (
    <div className="group relative flex items-center">
      <Radio.Group optionType="button" value={value} onChange={onChange}>
        {children}
      </Radio.Group>
      <div className="text-xs hidden group-hover:inline-block group-hover:text-neutral-600">
        <button
          className="ml-2 w-4 h-4"
          onClick={(e) => {
            onClear();
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <CloseCircleOutlined />
        </button>
      </div>
    </div>
  );
};

const ValidationModal = ({ id, task, getNext, onClose }) => {
  const { user } = useAuth();
  const { article, generic_article: genericArticle } = task;
  const queryClient = useQueryClient();
  const valTask = useQuery(['validation_task', id], () => ValidationTaskV3.get(id).then((res) => res), {
    cacheTime: 0,
  });

  const submitMutation = useMutation(
    (data) => {
      return ValidationTaskV3.saveAnnotation(id, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['validation_tasks']);
        valTask.refetch();
      },
    },
  );

  const deleteAnnotation = useMutation(
    (id) => {
      return ValidationTaskV3.deleteAnnotation(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['validation_tasks']);
        valTask.refetch();
      },
    },
  );

  const booleanFields = [
    'endpoints_correct',
    'drugs_studied_correct',
    'queried_drug_evaluated',
    'is_adhoc',
    'cancer_types_correct',
    'is_queried_drug_randomized',
    'serious_adverse_event',
    'is_difficult_to_validate',
  ];

  const handleSubmitWithNext = async (values) => {
    let res = await submitMutation.mutate({
      ...values,
      pubmed_val_meta_id: id,
      validator: user.username,

      pubmed_id: article.pubmed_id,
      drug: article.drug,
    });
    queryClient.invalidateQueries(['validation_tasks']);
    message.success('Validation saved successfully');
    getNext();
  };

  const convertToBoolean = (value) => {
    if (value === undefined || value === null) {
      return null;
    }
    return value === 'YES' ? true : false;
  };

  const [form] = Form.useForm();
  const annotation = valTask.data?.annotation ? valTask.data.annotation : null;
  const genericInitialData = {
    ...valTask.data?.generic_annotation,
    ...valTask.data?.generic_article,
  };
  const drugSpecificInitialData = {
    ...valTask.data?.article,
    ...valTask.data?.annotation,
  };

  const articleDetailQuery = useQuery(['articleDetail', task.article_id], () => {
    return DocService.get(task.article_id);
  });

  const articleDetail = articleDetailQuery.data;

  if (articleDetailQuery.isLoading) {
    return <div>Loading...</div>;
  }
  return (
    <Modal
      title={
        <div className="flex justify-between">
          <div>Validate Article</div>
          <div className="text-sm text-neutral-500 mr-8">
            <Button
              type="link"
              onClick={() => getNext()}
              className="text-blue-500 bg-blue-50 hover:bg-blue-100 rounded-sm px-2 py-1 flex items-center gap-2"
            >
              Next Task <ArrowRightIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      }
      open={id ? true : false}
      onCancel={() => {
        onClose();
      }}
      onOk={() => form.submit()}
      width={1400}
      destroyOnClose={true}
      footer={null}
    >
      {valTask.data && (
        <div className="flex gap-6 h-[80vh]">
          <div className="w-1/2 overflow-y-auto">
            <div className="flex flex gap-2">
              <button
                className="text-sm text-blue-500 bg-blue-50 rounded-sm px-2 py-1 flex items-center gap-2"
                onClick={() => {
                  window.open(`https://pubmed.ncbi.nlm.nih.gov/${articleDetail.pubmed_id}`, '_blank');
                }}
              >
                <Link2Icon className="w-4 h-4 text-blue-500" />
                {articleDetail.pubmed_id || 'N/A'}
              </button>
              <div className="text-sm text-blue-500 bg-blue-50 rounded-sm px-2 py-1 flex items-center gap-2">
                <Medicine02Icon className="w-4 h-4 text-blue-500" />
                {article.drug}
              </div>
              <div className="text-sm text-blue-500 bg-blue-50 rounded-sm px-2 py-1 flex items-center gap-2">
                <Dna01Icon className="w-4 h-4 text-blue-500" />
                {article.disease || 'N/A'}
              </div>
            </div>
            <div className="text-sm font-semibold mt-4">Title</div>
            <div className="text-sm">{genericArticle.title}</div>

            <div className="text-sm font-semibold  mt-4 ">Abstract</div>
            <div className="text-sm leading-[175%] ">{genericArticle.abstract}</div>

            <div className="text-sm font-semibold mt-8 text-blue-500">Extracted Details</div>
            <ExtractedDetails article={articleDetail} />
          </div>
          <div className="w-1/2 overflow-y-auto">
            <div>
              <GenericForm
                initialValues={genericInitialData}
                validationTaskId={id}
                articleId={task.article_id}
                validator={user.username}
                isValidated={valTask.data.generic_annotation !== null && valTask.data.generic_annotation !== undefined}
              />
              <DrugSpecificForm
                initialValues={drugSpecificInitialData}
                validationTaskId={id}
                articleId={task.article_id}
                validator={user.username}
                isValidated={valTask.data.annotation !== null && valTask.data.annotation !== undefined}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

const ValidationCard = ({ data, onSelect }) => {
  return (
    <div
      className="border border-neutral-200 rounded-lg p-4 flex flex-col justify-between"
      //   onClick={() => onSelect(data)}
    >
      <div>
        <div className="flex gap-4 text-xs">
          {data.validation_status === 'pending' ? (
            <span className="text-orange-500 bg-orange-100 px-2 py-1 rounded-md">Pending</span>
          ) : (
            <span className="text-green-500 bg-green-100 px-2 py-1 rounded-md">Completed</span>
          )}
          <div className="text-neutral-500 bg-neutral-100 px-2 py-1 rounded-md">PMID: {data.article.pubmed_id}</div>
          <div className="text-neutral-500 bg-neutral-100 px-2 py-1 rounded-md flex items-center gap-2">
            <Medicine02Icon className="w-4 h-4" /> {data.article.drug}
          </div>
        </div>
        <div className="text-sm text-neutral-600 mt-4 font-semibold">{data.generic_article?.title}</div>
      </div>
      <div className="flex mt-4">
        <Button onClick={() => onSelect(data)}>Validate</Button>
      </div>
    </div>
  );
};

const ValidationV2View = () => {
  const match = useMatch('/validationV2/:id');

  const [state, setState] = useState({
    loading: true,
    page: 1,
    search: '',
  });

  const [selectedTask, setSelectedTask] = useState(null);

  const [filter, setFilter] = useState({
    status: '',
    validator: '',
    search: '',
  });

  const validationTasks = useQuery(
    ['validation_tasks', state.page, state.search, filter.status, filter.validator],
    () =>
      ValidationTaskV3.getTasks({
        page: state.page,
        search: state.search,
        validation_set_id: match?.params.id,
        status: filter.status,
        validator: filter.validator,
      }),
    {},
  );
  const getNext = () => {
    ValidationTaskV3.getNext(selectedTask.id, {
      validation_set_id: match.params.id,
    }).then((res) => {
      setSelectedTask(res);
    });
  };

  const validators = useQuery(['validators'], () =>
    ValidationTaskV3.getValidators({
      validation_set_id: match.params.id,
    }),
  );

  return (
    <div>
      <h2 className="text-2xl font-semibold text-neutral-800">Validation View</h2>

      <div className="mt-4 flex gap-4">
        <Input.Search
          placeholder="Search by article title"
          onSearch={(value) => {
            setState((prev) => ({ ...prev, search: value }));
          }}
        />
        <Select
          placeholder="Select validator"
          onChange={(value) => {
            setFilter((prev) => ({ ...prev, validator: value }));
          }}
          options={validators.data?.results.map((validator) => ({
            label: validator,
            value: validator,
          }))}
        />
        <Select
          placeholder="Select status"
          onChange={(value) => {
            setFilter((prev) => ({ ...prev, status: value }));
          }}
        >
          <Select.Option value="">All</Select.Option>
          <Select.Option value="pending">Pending</Select.Option>
          <Select.Option value="completed">Completed</Select.Option>
        </Select>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4">
        {validationTasks.data && !validationTasks.isLoading
          ? validationTasks.data.results.map((task) => (
              <ValidationCard data={task} onSelect={() => setSelectedTask(task)} key={task._id} />
            ))
          : null}
      </div>

      <Pagination
        className="my-4 flex justify-end"
        current={state.page}
        pageSize={10}
        total={validationTasks.data?.total || 0}
        onChange={(page) => setState((prev) => ({ ...prev, page }))}
      />
      {selectedTask && (
        <ValidationModal
          id={selectedTask ? selectedTask._id : null}
          task={selectedTask ? selectedTask : null}
          getNext={getNext}
          onClose={() => setSelectedTask(null)}
        />
      )}
    </div>
  );
};

export default ValidationV2View;
